import moment from 'moment-timezone';
import { useEffect, useRef, useState } from 'react';
/**
 * Custom Hook para verificar se a hora atual está dentro de algum dos intervalos de programação do paramêtro.
 *
 * Essa verificação é feita a cada 60 segundos.
 *
 * @export
 * @param {{ startTime: string, endTime: string}[]} automationIntervals - Intervalos de programação no padrão 'HH:mm'
 * @param { number| undefined } timezone - Timezone da unidade
 * @return {*}  {{
 *   isOutOfSchedule: boolean;
 * }}
 */
export function useVerifyOutOfSchedule(
  automationIntervals: { startTime: string, endTime: string}[],
  timezone: number | undefined,
): {
  isOutOfSchedule: boolean;
} {
  const [isOutOfSchedule, setIsOutOfSchedule] = useState(true);
  const intervalReference = useRef<NodeJS.Timeout | null>(null);

  function parseTimeStringToNumber(time: string): [number, number] {
    const splittedTime = time.split(':');
    if (splittedTime.length < 2) {
      throw Error('Not possible to format time.');
    }

    const numbers = splittedTime.map((time) => Number(time));

    return [numbers[0], numbers[1]];
  }

  function setHourMinutesToDate(date: Date, hours: number, minutes: number): Date {
    const newDate = new Date(date);
    newDate.setHours(hours, minutes, 0, 0);
    return newDate;
  }

  function hourToLocalHour(hour: number): number {
    const localOffset = moment().utcOffset() / 60;
    return hour - (timezone ?? -3) + localOffset;
  }

  function verifyIsOutOfSchedule(intervals: {startTime: string, endTime: string }[]): void {
    const now = new Date();

    const isOut = intervals.every((interval) => {
      const [hourIntervalStart, minutesIntervalStart] = parseTimeStringToNumber(interval.startTime);
      const [hourIntervalEnd, minutesIntervalEnd] = parseTimeStringToNumber(interval.endTime);

      const dateStart = setHourMinutesToDate(new Date(), hourToLocalHour(hourIntervalStart), minutesIntervalStart);
      const dateEnd = setHourMinutesToDate(new Date(), hourToLocalHour(hourIntervalEnd), minutesIntervalEnd);

      return now < dateStart || now > dateEnd;
    });

    setIsOutOfSchedule(isOut);
  }

  useEffect(() => {
    if (automationIntervals.length > 0) {
      verifyIsOutOfSchedule(automationIntervals);

      const intervalId = setInterval(() => {
        verifyIsOutOfSchedule(automationIntervals);
      }, 10000);

      intervalReference.current = intervalId;
    }

    return () => {
      if (intervalReference.current) {
        clearInterval(intervalReference.current);
      }
    };
  }, [automationIntervals]);

  useEffect(() => () => {
    if (intervalReference.current) {
      clearInterval(intervalReference.current);
    }
  }, []);

  return {
    isOutOfSchedule,
  };
}
